import { useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Logo } from '@/components/atoms/Logo';
import { StatusBadge } from '@/components/atoms/StatusBadge';
import { ruoloMeta } from '@/lib/statusMaps';
import { initials } from '@/lib/format';
import { tourRoleFor } from '@/components/tour/tours';
import { useTour } from '@/components/tour/useTour';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { useUnreadCount } from '@/hooks/useNotifiche';
import { useTenantScope } from '@/hooks/useTenantScope';
import { useUIStore } from '@/stores/useUIStore';
import { homePath, paths, ticketCreateHref } from '@/routes/paths';
import { AppuntamentoFormDialog } from './AppuntamentoFormDialog';
import { AssistantLauncher } from './AIAssistant';
import { NotificationsMenu } from './NotificationsMenu';

export const MOBILE_NAV_DRAWER_ID = 'app-mobile-nav';

function commandShortcut(): string {
  if (typeof navigator === 'undefined') return 'Ctrl K';
  return /Mac|iPhone|iPad/.test(navigator.userAgent) ? '⌘K' : 'Ctrl K';
}

export interface TopbarProps {
  onOpenPalette: () => void;
}

/**
 * Fixed application bar: mobile nav trigger, notifications and the account menu.
 *
 * It stays pinned to the viewport, to the right of the fixed sidebar from `sm` up.
 * On `xs`: hamburger + logo + search + notifications + account. New ticket
 * lives in the bottom bar. On `sm+` it is the primary button here. The tenant
 * switcher lives in the sidebar’s area list (mobile drawer on `xs`) so this
 * bar stays clear. Only one switcher is mounted — dual mount races URL sync.
 */
export function Topbar({ onOpenPalette }: TopbarProps): JSX.Element {
  const { user, logout } = useAuth();
  const { startTour } = useTour();
  const { canCreateTickets, isStaff } = usePermissions();
  const tenantScope = useTenantScope();
  const newTicketHref = ticketCreateHref({ clienteId: isStaff ? tenantScope : undefined });
  const { data: unread } = useUnreadCount();
  const unreadCount = unread?.count ?? 0;
  const mobileNavOpen = useUIStore((state) => state.mobileNavOpen);
  const setMobileNavOpen = useUIStore((state) => state.setMobileNavOpen);
  const toggleTheme = useUIStore((state) => state.toggleTheme);
  const themePreference = useUIStore((state) => state.themePreference);
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [appointmentOpen, setAppointmentOpen] = useState(false);

  const handleLogout = async (): Promise<void> => {
    setAnchorEl(null);
    await logout();
    void navigate(paths.home, { replace: true });
  };

  const roleMeta = user ? ruoloMeta[user.ruolo] : null;

  return (
    <AppBar
      position="fixed"
      elevation={0}
      color="default"
      sx={(theme) => ({
        backgroundColor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'border.hairline',
        backgroundImage: 'none',
        boxShadow: 'none',
        pt: 'env(safe-area-inset-top, 0px)',
        pl: 'env(safe-area-inset-left, 0px)',
        pr: 'env(safe-area-inset-right, 0px)',
        '&&': {
          top: 0,
          left: 0,
          width: '100%',
          [theme.breakpoints.up('sm')]: {
            left: theme.layout.sidebarWidth,
            width: `calc(100% - ${theme.layout.sidebarWidth}px)`,
          },
        },
      })}
    >
      <Toolbar
        sx={{
          gap: 2,
          minHeight: { xs: 56, md: 48 },
          px: { xs: 2, md: 3 },
        }}
      >
        <IconButton
          onClick={() => setMobileNavOpen(true)}
          aria-label="Apri il menu di navigazione"
          aria-controls={MOBILE_NAV_DRAWER_ID}
          aria-expanded={mobileNavOpen}
          sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
        >
          <MenuIcon />
        </IconButton>

        <Box
          sx={{
            display: { xs: 'flex', sm: 'none' },
            alignItems: 'center',
            minWidth: 0,
            flexShrink: 1,
          }}
        >
          <Logo size="small" to={homePath(user?.ruolo)} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, display: { xs: 'none', md: 'flex' }, justifyContent: 'center', px: 2 }}>
          <Button
            data-tour="search"
            onClick={onOpenPalette}
            startIcon={<SearchOutlinedIcon aria-hidden />}
            aria-label={`Apri la ricerca, scorciatoia ${commandShortcut()}`}
            sx={{
              width: '100%',
              maxWidth: 440,
              justifyContent: 'flex-start',
              gap: 1,
              color: 'text.secondary',
              backgroundColor: 'background.default',
              border: '1px solid',
              borderColor: 'divider',
              px: 1.5,
              '&:hover': {
                backgroundColor: 'action.hover',
                borderColor: 'primary.main',
                color: 'text.primary',
              },
            }}
          >
            <Box component="span" sx={{ flex: 1, textAlign: 'left', fontWeight: 500 }}>
              Cerca o vai a una pagina
            </Box>
            <Box
              component="span"
              aria-hidden
              sx={{
                fontFamily: (theme) => theme.fontFamilyMono,
                fontSize: '0.6875rem',
                letterSpacing: '0.04em',
                border: '1px solid',
                borderColor: 'divider',
                px: 0.75,
                py: 0.25,
                color: 'text.secondary',
              }}
            >
              {commandShortcut()}
            </Box>
          </Button>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, display: { xs: 'block', md: 'none' } }} />

        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', flexShrink: 0 }}>
          {user ? (
            <Box sx={{ display: 'inline-flex', flexShrink: 0, mr: 0.5 }}>
              <Button
                component={RouterLink}
                to={paths.calendario}
                variant="outlined"
                size="small"
                startIcon={<CalendarMonthOutlinedIcon />}
                aria-label="Calendario"
                data-tour="calendar"
                sx={{
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  px: { xs: 1, sm: 1.5 },
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  Calendario
                </Box>
              </Button>
              <Tooltip title="Nuovo appuntamento">
                <Button
                  variant="outlined"
                  size="small"
                  aria-label="Nuovo appuntamento"
                  onClick={() => setAppointmentOpen(true)}
                  sx={{
                    minWidth: 36,
                    px: 1,
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    borderLeft: 0,
                  }}
                >
                  <AddIcon fontSize="small" />
                </Button>
              </Tooltip>
            </Box>
          ) : null}
          {canCreateTickets && (
            <Button
              component={RouterLink}
              to={newTicketHref}
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              data-tour="new-ticket"
              sx={{
                display: { xs: 'none', sm: 'inline-flex' },
                flexShrink: 0,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                mr: 1,
              }}
            >
              Nuovo ticket
            </Button>
          )}

          <Tooltip title="Cerca (Ctrl+K)">
            <IconButton
              onClick={onOpenPalette}
              aria-label="Apri la command palette"
              data-tour="search"
              sx={{ display: { xs: 'inline-flex', md: 'none' } }}
            >
              <SearchOutlinedIcon />
            </IconButton>
          </Tooltip>

          <AssistantLauncher />
          <NotificationsMenu />

          <Tooltip title="Account">
            <IconButton
              onClick={(event) => setAnchorEl(event.currentTarget)}
              aria-label="Apri il menu account"
              aria-haspopup="menu"
              aria-expanded={Boolean(anchorEl)}
              data-tour="profile-menu"
            >
              <Avatar
                sx={{
                  width: 34,
                  height: 34,
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                }}
              >
                {initials(user?.nome)}
              </Avatar>
            </IconButton>
          </Tooltip>
        </Stack>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { width: { xs: 'min(260px, calc(100vw - 24px))', sm: 260 }, mt: 1 } } }}
        >
          <Box sx={{ px: 4, py: 3 }}>
            <Typography variant="subtitle2" noWrap>
              {user?.nome}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {user?.email}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
              {roleMeta && <StatusBadge label={roleMeta.label} tone={roleMeta.tone} />}
              {user?.cliente_ragione_sociale && (
                <StatusBadge label={user.cliente_ragione_sociale} tone="neutral" />
              )}
            </Stack>
          </Box>

          <Divider />

          <MenuItem
            onClick={() => {
              toggleTheme();
              setAnchorEl(null);
            }}
          >
            <ListItemIcon>
              {themePreference === 'light' ? (
                <DarkModeOutlinedIcon fontSize="small" />
              ) : (
                <LightModeOutlinedIcon fontSize="small" />
              )}
            </ListItemIcon>
            {themePreference === 'light' ? 'Tema scuro' : 'Tema chiaro'}
          </MenuItem>

          <MenuItem component={RouterLink} to={paths.notifiche} onClick={() => setAnchorEl(null)}>
            <ListItemIcon>
              <NotificationsNoneOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Notifiche{unreadCount > 0 ? ` (${unreadCount})` : ''}
          </MenuItem>

          <MenuItem
            onClick={() => {
              startTour(tourRoleFor(user?.ruolo));
              setAnchorEl(null);
            }}
          >
            <ListItemIcon>
              <RouteOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Rivedi il tour
          </MenuItem>

          <MenuItem component={RouterLink} to={paths.impostazioni} onClick={() => setAnchorEl(null)}>
            <ListItemIcon>
              <SettingsOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Impostazioni
          </MenuItem>

          <MenuItem onClick={() => void handleLogout()} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <LogoutOutlinedIcon fontSize="small" color="error" />
            </ListItemIcon>
            Esci
          </MenuItem>
        </Menu>
      </Toolbar>
      {appointmentOpen ? <AppuntamentoFormDialog open onClose={() => setAppointmentOpen(false)} /> : null}
    </AppBar>
  );
}
